import React, { useRef, SyntheticEvent } from 'react';

import {
    Wrapper,
    Image,
    MainContent,
    Heading,
    Preamble,
    ReasonToJoin,
    RegButton,
    LoginButton,
    ButtonsWrapper,
    StyledTickWrapper,
    Ticks
} from './RegistrationAndLogin.styles';
import homepageTranslations from 'public/locales/en/homepage.json';
import regAndSignInSvg from 'public/assets/homepage/reg-login.svg';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';
import triggerTracking from '@blueprint/tracking';

const t = getTranslator(homepageTranslations.registration);

const headlineText = t('headline');
const preambleText = t('join_zoopla.preamble');
const saveFavouritesText = t('join_zoopla.save_favorites');
const setupAlertsText = t('join_zoopla.setup_alerts');
const guidesText = t('join_zoopla.guides');
const registrationButtonText = t('registration_button');
const signInButtonText = t('sign_in_button');

const regAndSignInSvgDimensions = { width: 589, height: 331 };

type Props = {
    openAuthModal: (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: AuthModalMode
    ) => void;
};

export const RegistrationAndLogin = ({ openAuthModal }: Props) => {
    const registerTriggerOrigin = useRef(null);
    const signInTriggerOrigin = useRef(null);

    const handleClickRegister = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'clickthrough',
            eventLabel: 'register'
        });
        openAuthModal(registerTriggerOrigin, 'register');
    };

    const handleClickSignIn = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'clickthrough',
            eventLabel: 'sign_in'
        });
        openAuthModal(signInTriggerOrigin, 'sign-in');
    };

    return (
        <Wrapper data-testid="registration-and-login">
            <Image
                src={regAndSignInSvg}
                width={regAndSignInSvgDimensions.width}
                height={regAndSignInSvgDimensions.height}
                alt=""
            />
            <MainContent>
                <Heading size="6">{headlineText}</Heading>
                <Preamble>{preambleText}</Preamble>
                <StyledTickWrapper>
                    <Ticks />
                    <ReasonToJoin>{setupAlertsText}</ReasonToJoin>
                </StyledTickWrapper>
                <StyledTickWrapper>
                    <Ticks />
                    <ReasonToJoin>{saveFavouritesText}</ReasonToJoin>
                </StyledTickWrapper>
                <StyledTickWrapper>
                    <Ticks />
                    <ReasonToJoin>{guidesText}</ReasonToJoin>
                </StyledTickWrapper>
                <ButtonsWrapper>
                    <RegButton
                        href="/register/"
                        ref={registerTriggerOrigin}
                        data-testid="reg-and-login-button-register"
                        variant="primary"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            handleClickRegister();
                        }}
                    >
                        {registrationButtonText}
                    </RegButton>
                    <LoginButton
                        href="/signin/"
                        ref={signInTriggerOrigin}
                        data-testid="reg-and-login-button-sign-in"
                        variant="tertiary"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            handleClickSignIn();
                        }}
                    >
                        {signInButtonText}
                    </LoginButton>
                </ButtonsWrapper>
            </MainContent>
        </Wrapper>
    );
};
